'use client';

import {useEffect, useState} from 'react';

import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';
import {FooterServerData} from '@udemy-v2/react-footer';
import {useI18n} from '@udemy-v2/i18n';

/**
 * Fetches footer data from the API
 * @param locale - Optional locale identifier for language preference (e.g., 'en_US', 'es_ES')
 * @returns Promise<FooterServerData> The footer data
 * @throws Error if the API request fails
 */
export async function fetchFooterData(locale?: string): Promise<FooterServerData> {
    const headers = {
        ...getMonolithApiHeaders(),
    };

    // Add Accept-Language header if locale is provided
    if (locale) {
        // Convert locale format from 'en_US' to 'en-US' for HTTP header
        const acceptLanguage = locale.replace('_', '-');
        headers['Accept-Language'] = acceptLanguage;
    }

    const response = await fetch('/api-2.0/contexts/me/?footer=true', {
        method: 'GET',
        headers,
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch footer context: ${response.status} ${response.statusText}`,
        );
    }

    const data: {footer: FooterServerData} = await response.json();

    return data.footer;
}

export const useFooterData = () => {
    const [footerServerData, setFooterServerData] = useState<FooterServerData | null>(null);
    const {locale} = useI18n();

    useEffect(() => {
        const fetchFooterContext = async () => {
            try {
                const footerData = await fetchFooterData(locale);
                setFooterServerData(footerData);
            } catch (error) {
                console.error('Failed to fetch footer data:', error);
                // Keep footerServerData as null on error
            }
        };

        fetchFooterContext();
    }, [locale]);

    return footerServerData;
};
